import {
  AlertTriangle, ShieldCheck, Wallet, Vote, Lock, Radio, Scale, Users,
  GitBranch, ClipboardCheck, Satellite, Fingerprint, Globe,
} from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, CTABanner, Slab, MockVisual, StatBlock } from '@/components/marketing/primitives.jsx';

const subsystems = [
  {
    icon: AlertTriangle,
    title: 'RERR',
    description:
      'Random Event Reporting & Response — any citizen or officer can file a safety, crime, or suspicious-activity report with chain-of-custody from device to dispatch.',
    to: '/subsystems/rerr',
  },
  {
    icon: ShieldCheck,
    title: 'IFPS',
    description:
      'Identity Fraud Prevention System — real-time challenges, identity-state tracking, and coordinated dispute handling across institutional partners.',
    to: '/subsystems/ifps',
  },
  {
    icon: Wallet,
    title: 'FIRL',
    description:
      'Funds Integrity Routing Layer — gated federal fund flow with anomaly detection, authoritative objectives, and full enforcement audit.',
    to: '/subsystems/firl',
  },
  {
    icon: Vote,
    title: 'VERIS',
    description:
      'Voter Eligibility & Registration Integrity — deterministic + probabilistic dedup, cross-jurisdiction state machines, and election-official workflow.',
    to: '/subsystems/veris',
  },
];

const pillars = [
  {
    icon: Lock,
    title: 'Cryptographic integrity, not trust-me',
    description:
      'Every record — report, decision, signal — is hash-chained and digitally signed. Integrity can be verified independently of NACIS.',
  },
  {
    icon: Users,
    title: 'Human-in-the-loop by design',
    description:
      'AI surfaces signals and prioritises work. Humans render determinations. State machines and RBAC enforce that boundary in code.',
  },
  {
    icon: GitBranch,
    title: 'Federated, not centralised',
    description:
      'Agencies, institutions, and jurisdictions operate their own endpoints; NACIS is the shared trust fabric. No single owner, no single choke-point.',
  },
  {
    icon: ClipboardCheck,
    title: 'Append-only audit everywhere',
    description:
      'Every privileged action writes to an append-only master audit log with Merkle-style hash chaining, exportable as WORM bundles.',
  },
  {
    icon: Radio,
    title: 'Real-time or nothing',
    description:
      'Private WebSocket channels deliver dispatch, alert, and challenge events with sub-second latency — gated by role at every subscription.',
  },
  {
    icon: Scale,
    title: 'Judicial warrant packaging',
    description:
      'When a matter needs a warrant, NACIS packages the evidence, routes it to the right reviewer, and preserves the signed outcome.',
  },
];

const trustCards = [
  {
    icon: Satellite,
    title: 'OSINT fusion — public data only',
    description:
      'Allow-listed public feeds inform analyst workflows. Raw URLs and keywords are hashed at ingest; correlations are advisory, never autonomous.',
  },
  {
    icon: Fingerprint,
    title: 'Device-attested reports',
    description:
      'Every RERR submission carries a device fingerprint + authenticity score. Silent-activation reports are tagged and federated to USSS when protectees are in scope.',
  },
  {
    icon: Globe,
    title: 'Cross-sector dependency graph',
    description:
      'CSDR + CDMS map every credential, institution, and program so that one upstream change triggers every downstream alert automatically.',
  },
];

export default function Home() {
  return (
    <>
      <Hero
        eyebrow="National trust fabric"
        title={<>Civic reporting, identity integrity, and inter-agency coordination — on one verifiable trust fabric.</>}
        description="NACIS is the National AI Communications-Intelligence System: a federated platform that connects citizens, agencies, institutions, and courts with cryptographic chain-of-custody at every step."
        primaryCta={{ to: '/for-agencies', label: 'Request agency access' }}
        secondaryCta={{ to: '/subsystems',  label: 'Explore subsystems' }}
      />

      <section className="border-b border-slate-200 bg-slate-50 py-10">
        <div className="container-wide">
          <div className="grid grid-cols-2 items-center gap-6 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 md:grid-cols-5">
            <div>Department of Justice</div>
            <div>Department of Homeland Security</div>
            <div>Department of the Treasury</div>
            <div>Election Assistance Commission</div>
            <div>State & Local Law Enforcement</div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Four subsystems, one fabric"
            title="Purpose-built for the highest-stakes civic workflows."
            description="Every subsystem enforces the same integrity contract: device-attested intake, role-based review, append-only audit, and signed outputs."
          />
          <div className="mt-12">
            <FeatureGrid items={subsystems} columns={4} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="Priority Index"
        title="AI-assisted triage, auditable in every weight."
        description="NACIS computes a 4-domain Priority Index for every report — threat, device, media, and context — with transparent scoring, national-security elevations, and USSS fast-path for protectee-related events."
        bullets={[
          'Threat-domain weighting is declared in config, not hidden in models.',
          'Silent-activation and protected-person submissions bypass standard queues.',
          'Every PI recomputation is logged with its full input delta.',
        ]}
        image={<MockVisual accent="sky" lines={6} />}
      />

      <Slab
        eyebrow="Integrity by construction"
        reverse
        title="Hash-chained, digitally signed, independently verifiable."
        description="Every privileged action — a dispatch, a dedup match, a warrant approval, a BOLO — is written to a per-entity integrity chain and mirrored into the master audit log. Export any slice as a WORM bundle and verify it outside NACIS."
        bullets={[
          'Merkle-style SHA-256 chains per entity and across the audit log.',
          'HMAC-SHA256 signatures on every human decision.',
          'WORM bundles with canonical JSON — re-verify without our code running.',
        ]}
        image={<MockVisual accent="emerald" lines={5} />}
      />

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="How we build trust"
            title="Six pillars, enforced in code, not in policy decks."
          />
          <div className="mt-12">
            <FeatureGrid items={pillars} columns={3} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <div className="grid items-end gap-8 md:grid-cols-2">
            <SectionHeader
              eyebrow="By the numbers"
              title="Built for national scale from day one."
              description="Designed for sustained throughput with cryptographic integrity preserved end-to-end."
            />
          </div>
          <div className="mt-12">
            <StatBlock
              stats={[
                { value: '4',          label: 'Primary subsystems (RERR, IFPS, FIRL, VERIS)' },
                { value: '10+',        label: 'Supporting services (Hub, CSDR, PMICS, CDMS, Judicial, Vision, OSINT, Aviation, Camera, Biometric)' },
                { value: 'Sub-second', label: 'Real-time dispatch & alert broadcasts' },
                { value: '100%',       label: 'Privileged actions written to append-only audit' },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Supporting services"
            title="The infrastructure beneath the visible subsystems."
          />
          <div className="mt-12">
            <FeatureGrid items={trustCards} columns={3} />
          </div>
        </div>
      </section>

      <CTABanner
        title="Bring your agency, institution, or jurisdiction onto NACIS."
        description="Tiered onboarding from observer to fully-federated partner. All integrations run through the Safe Entity Attestation + Interoperability Sandbox before production."
        primaryCta={{ to: '/contact',       label: 'Talk to our team' }}
        secondaryCta={{ to: '/for-partners', label: 'Partnership tiers' }}
      />
    </>
  );
}
